// src/components/dashboard/ClientCountReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";

// Guaranteed MUI Core Icons
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const ClientCountReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter Form States
  const [level1Group, setLevel1Group] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Table Data & Pagination States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [countRows, setCountRows] = useState([
    { id: 1, count: 17 },
    { id: 2, count: 17 },
  ]);

  // Clinical Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const addGreen = "#057A44";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setLevel1Group("");
    setShowActiveOnly(false);
  };

  const handleSearch = () => {
    // Search trigger
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PeopleAltRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Client Count Report
            </Typography>
            <Chip
              label="Analytics"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Analyze client counts by various groupings
          </Typography>
        </Box>
      </Box>

      {/* 2. Configure Grouping Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TuneRoundedIcon sx={{ color: labelTeal, fontSize: "1.25rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Configure Grouping
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 1 }}>
          Group By Levels (Max 6):
        </Typography>

        {/* Level 1 Input Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "#CBD5E1"}`,
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: controlBg,
            mb: 2,
          }}
        >
          <Box
            sx={{
              px: 2.2,
              py: 1,
              borderRight: `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "#CBD5E1"}`,
              fontWeight: 700,
              fontSize: "0.88rem",
              color: primaryText,
              backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC",
            }}
          >
            1
          </Box>
          <Box sx={{ flex: 1 }}>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={level1Group}
              onChange={(e) => setLevel1Group(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) =>
                selected ? (
                  selected
                ) : (
                  <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>
                    Select Level 1 Group By
                  </Typography>
                )
              }
              sx={{
                "& fieldset": { border: "none" },
                fontSize: "0.85rem",
                color: primaryText,
                height: "38px",
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active/Inactive">Active/Inactive</MenuItem>
              <MenuItem value="Client Default Location">Client Default Location</MenuItem>
              <MenuItem value="Primary Staff">Primary Staff</MenuItem>
              <MenuItem value="Default Program">Default Program</MenuItem>
            </Select>
          </Box>
          <IconButton sx={{ color: addGreen, pr: 1.5 }}>
            <AddCircleOutlineRoundedIcon sx={{ fontSize: "1.45rem" }} />
          </IconButton>
        </Box>

        {/* Active Clients Checkbox */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                sx={{ color: tealButton, "&.Mui-checked": { color: tealMain } }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: primaryText }}>
                Show Only Active Clients
              </Typography>
            }
          />
        </Box>

        {/* Notice Disclaimer */}
        <Typography sx={{ fontSize: "0.74rem", color: secondaryText, lineHeight: 1.4, mb: 3 }}>
          NOTICE: Any total aside from "All", "Active/Inactive", "Client Default Location", "Primary Staff", and "Default Program" may contain duplicates due to clients being associated with multiple staff members, etc.
        </Typography>

        {/* Bottom Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 3,
              textTransform: "uppercase",
              boxShadow: "none",
              "&:hover": { backgroundColor: tealButton, boxShadow: "none" },
            }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 2.8,
              textTransform: "uppercase",
              "&:hover": { borderColor: tealMain, backgroundColor: "rgba(36, 94, 101, 0.05)" },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Patient Count Results Section */}
      <Box sx={{ mb: 1.5, px: 0.5 }}>
        <Typography sx={{ fontSize: "0.98rem", fontWeight: 800, color: primaryText }}>
          Patient Count Results ({countRows.length} records)
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ minHeight: "260px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: primaryText,
                    fontWeight: 800,
                    fontSize: "0.86rem",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.8,
                    px: 3,
                  },
                }}
              >
                <TableCell>Client Count</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {countRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" },
                      "& td": { borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"}`, py: 1.8, px: 3 },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 2,
                          py: 0.5,
                          borderRadius: "6px",
                          border: `1.5px solid ${tealButton}`,
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          color: tealButton,
                          backgroundColor: pastelTealBg,
                          minWidth: "48px",
                        }}
                      >
                        {row.count}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={countRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryText,
            "& .MuiTablePagination-select": { color: primaryText, fontWeight: 600 },
          }}
        />
      </Box>
    </Box>
  );
};

export default ClientCountReport;